import $ from "jquery";
import _ from "underscore";
import StateComponent from './StateComponent'

/**
 * Base class for custom views. Contains base
 * @param [$containerEl] jQuery selector for element, which is going to be view's container.
 *                       It's not necessary to specify it in constructor, it can can done in .init() method.
 * @constructor
 */
export default class View extends StateComponent
{
    constructor($containerEl = null) {
        super({})
        this.$containerEl = $containerEl
        this.$el = null

        // display css-property for saving via hide-show
        this._displayCssProp = null
        // children components (View.js) for delegate base methods calls
        this._childrenComponents = []
        // prevent handling changes while component not rendered
        this.stopHandlingStateChanges()
    }

    /**
     * Renders the view, calls before and after hooks and optionally calls the update function.
     * @param [$containerEl] jQuery selector for element, which is going to be view's container.
     *                       This is alternative to passing container element in the constructor,
     *                       which is useful for postponed render.
     * @returns {View}
     */
    init($containerEl)
    {
        if ($containerEl)
            this.$containerEl = $containerEl;

        this.beforeRender();
        this.render();
        this.afterRender();
        this.rendered = true;
        this.resumeHandlingStateChanges(false)

        if (this.shouldUpdateOnInit())
            this.update();

        return this;
    }

    /**
     * Abstract render function, should put the view into container.
     */
    render()
    {
    }

    /**
     * Before-render hook.
     */
    beforeRender()
    {
    }

    /**
     * After-render hook.
     */
    afterRender()
    {
    }

    /**
     * Abstract update function, which should render any dynamic changes in the view.
     */
    update()
    {
    }

    /**
     * Defines if .update() method will be called once on .init()
     */
    shouldUpdateOnInit()
    {
        // TODO: Eventually remove this. Currently it's needed for legacy views, which may have this field.
        if (_.isBoolean(this["updateOnInit"]))
            return this["updateOnInit"];

        return true;
    }

    /**
     * Shortcut to jQuery $.on() function for event binding.
     * @param eventName {String} Event name.
     * @param handler {Function} Event handler.
     */
    on(eventName, handler)
    {
        $(this).on(eventName, handler);
    }

    /**
     * Shortcut to jQuery $.trigger() function for event binding.
     * @param eventName {String} Event name.
     * @param data {object} Event data.
     */
    trigger(eventName, data)
    {
        $(this).trigger(eventName, data);
    }

    /**
     * Shortcut for binding a function to view's instance. Useful when you want some callback to be a class method.
     * @param f {Function} Function to bind.
     * @returns {Function}
     */
    bind(f)
    {
        return _.bind(f, this);
    }

    /**
     * Shortcut for finding view's sub-elements using jQuery.
     * @param selector jQuery selector.
     */
    find(selector)
    {
        return this.$el.find(selector);
    }

    /**
     * Shortcut for finding view's sub-elements using jQuery and binding a click handler.
     * @param selector jQuery selector.
     * @param handler {Function} Click handler.
     */
    findAndBindClick(selector, handler)
    {
        return this.find(selector).click(this.bind(handler));
    }

    /**
     * Shortcut for binding a click handler to the entire view.
     * @param handler {Function} Click handler.
     */
    bindClick(handler)
    {
        return this.$el.click(this.bind(handler));
    }

    /**
     * Shows view.
     */
    show()
    {
        this.resumeHandlingStateChanges()
        if (this._displayCssProp !== null && this._displayCssProp !== 'none') {
            this.$el.css('display', this._displayCssProp)
        } else {
            this.$el.show()
        }
        this.applyForChildrenComponents(child => child.show())
    }

    /**
     * Hides view.
     */
    hide()
    {
        this.stopHandlingStateChanges()
        this._displayCssProp = this.$el.css('display')
        this.$el.hide();
        this.applyForChildrenComponents(child => child.hide())
    }

    resumeHandlingStateChanges(handlePendingChanges) {
        super.resumeHandlingStateChanges(handlePendingChanges)
        this.applyForChildrenComponents(child => child.resumeHandlingStateChanges(handlePendingChanges))
    }

    stopHandlingStateChanges() {
        super.stopHandlingStateChanges()
        this.applyForChildrenComponents(child => child.stopHandlingStateChanges())
    }

    callStateChangeHandlersWithCurrentState() {
        super.callStateChangeHandlersWithCurrentState()
    }

    /**
     * Toggles view visibility, calling .show() if parameter is true and .hide() if false.
     * @param {Boolean} show
     */
    toggle(show)
    {
        if (show)
            this.show();
        else
            this.hide();
    }

    /**
     * Disables re-renders and updates for the view (only those initiated by standard functions).
     * Rendering is disabled for the rest of view's lifespan, which should be short.
     * This can be useful to prevent unwanted effects just before the view will be destroyed.
     */
    disableRendering()
    {
        this.render = _.noop;
        this.beforeRender = _.noop;
        this.afterRender = _.noop;
        this.update = _.noop;
    }

    /**
     * Makes sure that view's initial state is rendered by forcefully calling all state change handlers.
     * Should only be called once – please, do not abuse this method.
     */
    renderInitialState()
    {
        this.callStateChangeHandlersWithCurrentState();
    }

    /**
     * Add children components for delegate base methods
     * @param components
     */
    registerChildComponents(...components) {
        this._childrenComponents.push(...components)
    }

    getChildrenComponents() {
        return this._childrenComponents
    }

    /**
     * Apply function for every child component
     * @param fn
     */
    applyForChildrenComponents(fn = (child) => {}) {
        this._childrenComponents.forEach(fn)
    }

    getEl() {
        return this.$el
    }

    _getContainerEl() {
        return this.$containerEl
    }
}


// WEBPACK FOOTER //
// ./web/js/View.js